export interface RectLike {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type DropTarget = { kind: 'slot'; slotIndex: number } | { kind: 'table' };

/** Minimum share of the smaller rect (card vs slot) that must overlap for a
 *  card-rect hit to count as an intentional drop on that slot. */
const MIN_OVERLAP_RATIO = 0.25;

function area(r: RectLike): number {
  return Math.max(0, r.right - r.left) * Math.max(0, r.bottom - r.top);
}

function overlapArea(a: RectLike, b: RectLike): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

function contains(r: RectLike, x: number, y: number): boolean {
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

/**
 * Resolve where a dragged card was dropped. Players aim with the card they
 * are dragging, not with the pointer — the grab point can sit a full card
 * height below the card's top edge — so the pointer position alone misses
 * drops that visually land on a slot. Precedence:
 *   1. pointer inside a slot (the precise, explicit signal)
 *   2. the slot the card's own rect overlaps most (≥ MIN_OVERLAP_RATIO of
 *      the smaller rect)
 *   3. pointer or card centre inside the table area
 */
export function resolveDropTarget(
  pointer: { x: number; y: number },
  cardRect: RectLike | null,
  slots: Iterable<[number, RectLike]>,
  tableRect: RectLike | null,
): DropTarget | null {
  let bestIdx = -1;
  let bestOverlap = 0;
  let bestThreshold = 0;
  for (const [idx, rect] of slots) {
    if (contains(rect, pointer.x, pointer.y)) {
      return { kind: 'slot', slotIndex: idx };
    }
    if (cardRect) {
      const overlap = overlapArea(rect, cardRect);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestIdx = idx;
        bestThreshold = Math.min(area(rect), area(cardRect)) * MIN_OVERLAP_RATIO;
      }
    }
  }
  if (bestIdx >= 0 && bestOverlap >= bestThreshold && bestOverlap > 0) {
    return { kind: 'slot', slotIndex: bestIdx };
  }
  if (tableRect) {
    if (contains(tableRect, pointer.x, pointer.y)) return { kind: 'table' };
    if (cardRect) {
      const cx = (cardRect.left + cardRect.right) / 2;
      const cy = (cardRect.top + cardRect.bottom) / 2;
      if (contains(tableRect, cx, cy)) return { kind: 'table' };
    }
  }
  return null;
}
